import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  DataTable,
  SortableHeadCell,
  TABLE_ICON_CLASS,
  TableCell,
  TableEmptyRow,
  TableFacts,
  TableHead,
  TableItem,
  TableRow,
} from "@/components/data-table";
import { FilterBar } from "@/components/filter-bar";
import { FilterToggle } from "@/components/filter-toggle";
import { MultiSelect } from "@/components/multi-select";
import { ToolIcon } from "@/components/tool-icon";
import { ToolName } from "@/components/tool-name";
import { type McpToolSpeed, useConnectorOverview } from "@/lib/api";
import { formatNumber } from "@/lib/format";
import { useLanguage } from "@/lib/i18n/language";
import { withGroupText } from "@/lib/i18n/tools";
import { matchesSearch } from "@/lib/search";
import { factLine } from "@/lib/table-facts";
import { sortRows, useUrlTableSort } from "@/lib/table-sort";
import { toolLabel, useToolTitles } from "@/lib/tool-titles";
import { cn } from "@/lib/utils";

import { formatDuration } from "../utils";

/** Tool, calls, Odoo calls, median, p95, p95 wait, p95 slowest request, rate limited. */
const COLUMNS = 8;

const SORT_COLUMNS = {
  tool: "asc",
  calls: "desc",
  odoo: "desc",
  median: "desc",
  p95: "desc",
  wait: "desc",
  request: "desc",
  limited: "desc",
} as const;

type SortKey = keyof typeof SORT_COLUMNS;

/**
 * So the table fits its box below the 1096 px it has at 1440: a header of two
 * words may take two lines; when its own room is tight (a 1024 px window) the
 * Odoo calls leave, and tighter still (beside the full sidebar) the median
 * and the slowest request leave too. The p95 and the wait stay: they say how
 * fast a tool was.
 */
const TIGHT = {
  wrapHead: "@max-[1060px]:whitespace-normal",
  odoo: "@max-4xl:hidden",
  secondary: "@max-3xl:hidden",
} as const;

/** A cell's number, muted when it is nothing to look at. */
function figureClass(value: number | null | undefined): string {
  return cn("whitespace-nowrap tabular-nums", !value && "text-muted");
}

/**
 * How fast each tool was in the period: its calls and how many went to Odoo,
 * the median and the p95 of its duration, the p95 of its wait and of Odoo's
 * slowest request, and how many of its calls Odoo slowed down. Every column
 * sorts (kept in the URL); a search, the tool's group (from the catalogue)
 * and "Alleen afgeremd" filter it. When its room is tight some columns leave
 * (`TIGHT`), so it never scrolls sideways. On a phone the p95 duration stays
 * right, and the rest moves under the tool.
 */
export function SpeedTable({ rows }: { rows: readonly McpToolSpeed[] }) {
  const { t } = useTranslation();
  const language = useLanguage();
  const titles = useToolTitles();
  const overview = useConnectorOverview().data;
  const [search, setSearch] = useState("");
  const [chosenGroups, setChosenGroups] = useState<string[]>([]);
  const [onlyLimited, setOnlyLimited] = useState(false);
  const sort = useUrlTableSort<SortKey>({
    columns: SORT_COLUMNS,
    initial: { key: "p95", dir: "desc" },
  });

  if (rows.length === 0) {
    return (
      <p className="text-muted rounded-lg border py-8 text-center text-sm">
        {t("analytics.toolsEmpty")}
      </p>
    );
  }

  const groupOf = new Map((overview?.tools ?? []).map((tool) => [tool.name, tool.group]));
  const groups = withGroupText(overview?.groups ?? [], language).filter((group) =>
    rows.some((row) => groupOf.get(row.tool) === group.id),
  );
  const filtered = search !== "" || chosenGroups.length > 0 || onlyLimited;
  const reset = () => {
    setSearch("");
    setChosenGroups([]);
    setOnlyLimited(false);
  };
  const matching = rows.filter(
    (row) =>
      matchesSearch([titles.get(row.tool), row.tool], search) &&
      (chosenGroups.length === 0 || chosenGroups.includes(groupOf.get(row.tool) ?? "")),
  );
  const groupLabel =
    chosenGroups.length === 0
      ? t("tools.filter.allGroups")
      : groups
          .filter((group) => chosenGroups.includes(group.id))
          .map((group) => group.title)
          .join(", ");
  const shown = sortRows(
    matching.filter((row) => !onlyLimited || row.rate_limited > 0),
    sort.sort,
    (row, key) => {
      switch (key) {
        case "tool":
          return toolLabel(titles, row.tool);
        case "calls":
          return row.calls;
        case "odoo":
          return row.odoo_calls;
        case "median":
          return row.median_duration_ms;
        case "p95":
          return row.p95_duration_ms;
        case "wait":
          return row.wait_p95_ms;
        case "request":
          return row.request_p95_ms;
        case "limited":
          return row.rate_limited;
      }
    },
  );

  return (
    <div className="space-y-3">
      <FilterBar
        search={{ value: search, onChange: setSearch, label: t("tools.filter.search") }}
        columns={["wide", "auto"]}
        filtered={filtered}
        onReset={reset}
      >
        <MultiSelect
          size="sm"
          className="w-full"
          options={groups.map((group) => ({ value: group.id, label: group.title }))}
          value={chosenGroups}
          onChange={setChosenGroups}
          label={groupLabel}
          name={t("tools.filter.group")}
          allLabel={t("tools.filter.allGroups")}
        />
        <FilterToggle
          label={t("speed.onlyRateLimited")}
          count={matching.filter((row) => row.rate_limited > 0).length}
          pressed={onlyLimited}
          onPressedChange={setOnlyLimited}
          className="w-full justify-between"
        />
      </FilterBar>
      <DataTable label={t("speed.perTool")}>
        <TableHead>
          <SortableHeadCell {...sort.head("tool")}>{t("analytics.tool")}</SortableHeadCell>
          <SortableHeadCell {...sort.head("calls")} align="right" narrow="hide">
            {t("analytics.calls")}
          </SortableHeadCell>
          <SortableHeadCell
            {...sort.head("odoo")}
            align="right"
            narrow="hide"
            className={cn(TIGHT.wrapHead, TIGHT.odoo)}
          >
            {t("speed.columns.odooCalls")}
          </SortableHeadCell>
          <SortableHeadCell
            {...sort.head("median")}
            align="right"
            narrow="hide"
            className={TIGHT.secondary}
          >
            {t("speed.columns.median")}
          </SortableHeadCell>
          <SortableHeadCell {...sort.head("p95")} align="right" hint={t("speed.hints.p95")}>
            {t("speed.columns.p95")}
          </SortableHeadCell>
          <SortableHeadCell
            {...sort.head("wait")}
            align="right"
            narrow="hide"
            hint={t("speed.hints.wait")}
            className={TIGHT.wrapHead}
          >
            {t("speed.columns.wait")}
          </SortableHeadCell>
          <SortableHeadCell
            {...sort.head("request")}
            align="right"
            narrow="hide"
            hint={t("speed.hints.request")}
            className={cn(TIGHT.wrapHead, TIGHT.secondary)}
          >
            {t("speed.columns.request")}
          </SortableHeadCell>
          <SortableHeadCell
            {...sort.head("limited")}
            align="right"
            narrow="hide"
            hint={t("speed.hints.rateLimited")}
          >
            {t("speed.columns.rateLimited")}
          </SortableHeadCell>
        </TableHead>
        <tbody>
          {shown.map((row) => (
            <TableRow key={row.tool}>
              <TableCell>
                <TableItem
                  icon={<ToolIcon name={row.tool} className={TABLE_ICON_CLASS} />}
                  title={<ToolName name={row.tool} titles={titles} search={search} />}
                  facts={
                    <TableFacts>
                      {factLine([
                        t("chart.calls", { count: row.calls }),
                        t("speed.facts.median", {
                          duration: formatDuration(row.median_duration_ms),
                        }),
                        row.wait_p95_ms != null &&
                          t("speed.facts.wait", { duration: formatDuration(row.wait_p95_ms) }),
                        row.rate_limited > 0 &&
                          t("speed.facts.rateLimited", { count: row.rate_limited }),
                      ])}
                    </TableFacts>
                  }
                />
              </TableCell>
              <TableCell align="right" narrow="hide" className={figureClass(row.calls)}>
                {formatNumber(row.calls)}
              </TableCell>
              <TableCell
                align="right"
                narrow="hide"
                className={cn(figureClass(row.odoo_calls), TIGHT.odoo)}
              >
                {formatNumber(row.odoo_calls)}
              </TableCell>
              <TableCell
                align="right"
                narrow="hide"
                className={cn(figureClass(row.median_duration_ms), TIGHT.secondary)}
              >
                {formatDuration(row.median_duration_ms)}
              </TableCell>
              <TableCell align="right" className="font-medium whitespace-nowrap tabular-nums">
                {formatDuration(row.p95_duration_ms)}
              </TableCell>
              <TableCell align="right" narrow="hide" className={figureClass(row.wait_p95_ms)}>
                {formatDuration(row.wait_p95_ms)}
              </TableCell>
              <TableCell
                align="right"
                narrow="hide"
                className={cn(figureClass(row.request_p95_ms), TIGHT.secondary)}
              >
                {formatDuration(row.request_p95_ms)}
              </TableCell>
              <TableCell align="right" narrow="hide" className={figureClass(row.rate_limited)}>
                {formatNumber(row.rate_limited)}
              </TableCell>
            </TableRow>
          ))}
          {shown.length === 0 && (
            <TableEmptyRow colSpan={COLUMNS} onReset={reset}>
              {t("tools.filter.empty")}
            </TableEmptyRow>
          )}
        </tbody>
      </DataTable>
    </div>
  );
}
